import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NotificationsRepository } from "@/api/notifications/repository";
import { NotificationsService } from "@/api/notifications/service";
import { useNotificationsStore } from "@/store/notifications/slice";
import type {
  INotification,
  INotificationsPagination,
} from "@/types/notifications";

export const useFetchPaginatedNotifications = () => {
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const abortController = useRef<AbortController | null>(null);
  const fetchingRef = useRef<boolean>(false);
  const doneRef = useRef<boolean>(false);

  const repository = useMemo(() => {
    return new NotificationsRepository(new NotificationsService(), {
      abort: true,
    });
  }, []);

  const fetchNotifications = useCallback(
    (pagination: INotificationsPagination) => {
      if (doneRef.current || fetchingRef.current) {
        return Promise.reject();
      }

      fetchingRef.current = true;
      setIsPageLoading(true);

      abortController.current?.abort();

      abortController.current = new AbortController();
      const signal = abortController.current.signal;

      return repository
        .getAll(pagination, { signal })
        .then((response: INotification[]) => {
          if (response.length === 0 || response.length < pagination.page) {
            doneRef.current = true;
          }

          useNotificationsStore.getState().append(response);
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          fetchingRef.current = false;
          setIsPageLoading(false);
        });
    },
    [repository],
  );

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  return {
    fetchNotifications,
    isPageLoading,
  };
};
